import React, { useState } from 'react';
import { Mail, Phone, MapPin, Building, Globe, Eye, EyeOff } from 'lucide-react';
import FormInput from '../../FormInput';

// Composant PasswordInput défini en dehors pour éviter la perte de focus
const PasswordInput = ({ 
  label, 
  name, 
  value, 
  onChange, 
  error, 
  required, 
  placeholder, 
  minLength, 
  isConfirm = false,
  showPassword,
  onToggleVisibility
}) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <div className="relative">
      <input
        type={showPassword ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        minLength={minLength}
        className={`block w-full px-3 py-2 border rounded-md shadow-sm pr-10 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
        }`}
        required={required}
      />
      <button
        type="button"
        onClick={onToggleVisibility}
        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
      >
        {showPassword ? (
          <EyeOff className="h-4 w-4" />
        ) : (
          <Eye className="h-4 w-4" />
        )}
      </button>
    </div>
    {error && (
      <p className="text-sm text-red-600">{error}</p>
    )}
  </div>
);

const EnseignantExterneForm = ({ 
  formData, 
  formErrors, 
  selectedEnseignant, 
  onInputChange, 
  onSubmit,
  submitting 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const titres = [
    { value: 'DR', label: 'Docteur' },
    { value: 'PR', label: 'Professeur' },
    { value: 'MC', label: 'Maître de Conférences' },
    { value: 'HDR', label: 'Habilité à Diriger des Recherches' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <form id="enseignant-form" onSubmit={onSubmit} className="space-y-6">
      {!selectedEnseignant && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-3">Informations du compte</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Nom d'utilisateur"
              type="text"
              name="username"
              value={formData.username}
              onChange={onInputChange}
              placeholder="nom.prenom"
              required
              error={formErrors.username}
            />

            <FormInput
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={onInputChange}
              placeholder="email@exemple.com"
              required
              error={formErrors.email}
              icon={Mail}
            />

            <FormInput
              label="Prénom"
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={onInputChange}
              required
              error={formErrors.first_name}
            />

            <FormInput
              label="Nom"
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={onInputChange}
              required
              error={formErrors.last_name}
            />

            <PasswordInput
              label="Mot de passe"
              name="password"
              value={formData.password}
              onChange={onInputChange}
              placeholder="Minimum 8 caractères"
              required
              error={formErrors.password}
              minLength={8}
              showPassword={showPassword}
              onToggleVisibility={togglePasswordVisibility}
            />

            <PasswordInput
              label="Confirmer le mot de passe"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={onInputChange}
              placeholder="Répétez le mot de passe"
              required
              error={formErrors.confirmPassword}
              minLength={8}
              showPassword={showConfirmPassword}
              onToggleVisibility={toggleConfirmPasswordVisibility}
            />
          </div>
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="text-md font-medium text-gray-900 mb-3">Informations professionnelles</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Titre"
            type="select"
            name="titre"
            value={formData.titre}
            onChange={onInputChange}
            options={[
              { value: '', label: 'Sélectionner un titre' },
              ...titres
            ]}
            required
            error={formErrors.titre}
          />

          <FormInput
            label="Spécialité"
            type="text"
            name="specialite"
            value={formData.specialite}
            onChange={onInputChange}
            placeholder="Ex: Informatique, Économie..."
            required
            error={formErrors.specialite}
            icon={Globe}
          />

          <div className="md:col-span-2">
            <FormInput
              label="Établissement d'origine"
              type="text"
              name="etablissement_origine"
              value={formData.etablissement_origine}
              onChange={onInputChange}
              placeholder="Nom de l'université/institution"
              required
              error={formErrors.etablissement_origine}
              icon={Building}
            />
          </div>

          <FormInput
            label="Grade"
            type="text"
            name="grade"
            value={formData.grade}
            onChange={onInputChange}
            placeholder="Grade académique"
            error={formErrors.grade}
          />

          <FormInput
            label="Téléphone"
            type="tel"
            name="telephone"
            value={formData.telephone}
            onChange={onInputChange}
            placeholder="+33 1 23 45 67 89"
            error={formErrors.telephone}
            icon={Phone}
          />

          <div className="md:col-span-2">
            <FormInput
              label="Adresse"
              type="textarea"
              name="adresse"
              value={formData.adresse}
              onChange={onInputChange}
              placeholder="Adresse complète..."
              error={formErrors.adresse}
              rows={3}
              icon={MapPin}
            />
          </div>
        </div>
      </div>

      <div className="text-sm text-gray-500">
        <span className="text-red-500">*</span> Champs obligatoires
      </div>
    </form>
  );
};

export default EnseignantExterneForm; 